/**
 * Casarto Model Selector
 * Performance-optimierte Version mit Smooth Cross-Fade
 */

class ModelSelector {
  constructor() {
    this.data = null;
    this.selectedModel = null;
    this.selectedBrand = null;
    this.hasUserInteracted = false;
    this.imageCache = new Map(); // Cache für geladene Bilder
    this.pendingTransitions = new Set(); // Track laufende Transitions
    
    // DOM Elements
    this.wrapper = document.querySelector('[data-model-selector]');
    if (!this.wrapper) return;
    
    // Desktop Elements
    this.trigger = this.wrapper.querySelector('[data-model-selector-trigger]');
    this.dropdown = this.wrapper.querySelector('[data-model-dropdown]');
    this.searchInput = this.wrapper.querySelector('[data-model-search]');
    this.modelList = this.wrapper.querySelector('[data-model-list]');
    this.clearButton = this.wrapper.querySelector('[data-model-clear]');
    this.label = this.wrapper.querySelector('[data-model-label]');
    
    // Mobile Elements
    this.mobileTrigger = this.wrapper.querySelector('[data-model-mobile-trigger]');
    this.modal = this.wrapper.querySelector('[data-model-modal]');
    this.modalOverlay = this.wrapper.querySelector('[data-modal-overlay]');
    this.modalClose = this.wrapper.querySelector('[data-modal-close]');
    this.searchInputMobile = this.wrapper.querySelector('[data-model-search-mobile]');
    this.modelListMobile = this.wrapper.querySelector('[data-model-list-mobile]');
    this.clearButtonMobile = this.wrapper.querySelector('[data-model-clear-mobile]');
    
    // Drawer Elements
    this.drawerTrigger = document.querySelector('[data-model-selector-drawer-trigger]');
    this.drawerDropdown = document.querySelector('[data-model-dropdown-drawer]');
    this.drawerSearchInput = document.querySelector('[data-model-search-drawer]');
    this.drawerModelList = document.querySelector('[data-model-list-drawer]');
    this.drawerClearButton = document.querySelector('[data-model-clear-drawer]');
    this.drawerLabel = document.querySelector('[data-model-label-drawer]');
    
    // Section Elements
    this.sectionTrigger = document.querySelector('[data-model-selector-section-trigger]');
    this.sectionDropdown = document.querySelector('[data-model-dropdown-section]');
    this.sectionSearchInput = document.querySelector('[data-model-search-section]');
    this.sectionModelList = document.querySelector('[data-model-list-section]');
    this.sectionClearButton = document.querySelector('[data-model-clear-section]');
    this.sectionLabel = document.querySelector('[data-model-label-section]');
    
    this.init();
  }

  getSelectionLabel() {
    return [this.selectedBrand, this.selectedModel].filter(Boolean).join(' ');
  }

  isMobileViewport() {
    return window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : window.innerWidth < 750;
  }
  
  init() {
    this.loadData();
    this.buildModelSlugSet();
    this.loadSavedModel();
    this.attachEventListeners();
    this.updateUI();
    
    if (document.readyState === 'complete') {
      this.applyModelToPage();
    } else {
      window.addEventListener('load', () => this.applyModelToPage());
    }
  }
  
  loadData() {
    const dataScript = document.querySelector('[data-model-selector-data]');
    if (dataScript) {
      try {
        this.data = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Model Selector: Failed to parse data', e);
      }
    }
  }

  buildModelSlugSet() {
    this.modelSlugSet = new Set();
    if (!this.data || !this.data.models) return;
    Object.values(this.data.models).forEach(models => {
      models.forEach(m => this.modelSlugSet.add(this.normalizeModelName(m).toLowerCase()));
    });
  }

  isKnownModelIdentifierFromText(text) {
    if (!text) return false;
    const re = /_([A-Za-z0-9-]+)_/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      const slug = m[1].toLowerCase();
      if (this.modelSlugSet && this.modelSlugSet.has(slug)) return true;
    }
    return false;
  }
  
  loadSavedModel() {
    const saved = this.getCookie(this.data?.cookieName);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.selectedBrand = parsed.brand;
        this.selectedModel = parsed.model;
        this.hasUserInteracted = true;
      } catch (e) {
        console.error('Model Selector: Failed to parse saved model', e);
      }
    } else {
      this.selectedBrand = 'Apple';
      this.selectedModel = 'iPhone 17 Pro';
      this.hasUserInteracted = false;
    }
  }
  
  attachEventListeners() {
    // Desktop
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggleDropdown());
    }
    
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce((e) => {
        this.filterModels(e.target.value, this.modelList);
      }, 200));
    }
    
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearSelection());
    }
    
    // Mobile
    if (this.mobileTrigger) {
      this.mobileTrigger.addEventListener('click', () => this.openModal());
    }
    
    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', () => this.closeModal());
    }
    
    if (this.modalClose) {
      this.modalClose.addEventListener('click', () => this.closeModal());
    }
    
    if (this.searchInputMobile) {
      this.searchInputMobile.addEventListener('input', this.debounce((e) => {
        this.filterModels(e.target.value, this.modelListMobile);
      }, 200));
    }
    
    if (this.clearButtonMobile) {
      this.clearButtonMobile.addEventListener('click', () => {
        this.clearSelection();
        this.closeModal();
      });
    }
    
    // Drawer
    if (this.drawerTrigger) {
      this.drawerTrigger.addEventListener('click', () => this.toggleDrawerDropdown());
    }
    
    if (this.drawerSearchInput) {
      this.drawerSearchInput.addEventListener('input', this.debounce((e) => {
        this.filterModels(e.target.value, this.drawerModelList);
      }, 200));
    }
    
    if (this.drawerClearButton) {
      this.drawerClearButton.addEventListener('click', () => {
        this.clearSelection();
        this.closeDrawerDropdown();
      });
    }
    
    // Section
    if (this.sectionTrigger) {
      this.sectionTrigger.addEventListener('click', () => this.toggleSectionDropdown());
    }
    
    if (this.sectionSearchInput) {
      this.sectionSearchInput.addEventListener('input', this.debounce((e) => {
        this.filterModels(e.target.value, this.sectionModelList);
      }, 200));
    }
    
    if (this.sectionClearButton) {
      this.sectionClearButton.addEventListener('click', () => {
        this.clearSelection();
        this.closeSectionDropdown();
      });
    }
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (this.wrapper && !this.wrapper.contains(e.target)) {
        this.closeDropdown();
      }
      
      const drawerSelector = document.querySelector('.model-selector--drawer');
      if (drawerSelector && !drawerSelector.contains(e.target)) {
        this.closeDrawerDropdown();
      }
      
      const sectionSelector = document.querySelector('.model-selector-section__search-container');
      if (sectionSelector && !sectionSelector.contains(e.target)) {
        this.closeSectionDropdown();
      }
    });
    
    // Listen for variant changes on product pages
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.syncWithVariantPicker(e.detail.variant);
      }
    });
  }
  
  toggleDropdown() {
    if (this.dropdown.hidden) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }
  
  openDropdown() {
    this.renderModelList(this.modelList);
    this.dropdown.hidden = false;
    this.searchInput && this.searchInput.focus();
  }
  
  closeDropdown() {
    if (!this.dropdown) return;
    this.dropdown.hidden = true;
    if (this.searchInput) this.searchInput.value = '';
  }
  
  openModal() {
    this.renderModelList(this.modelListMobile);
    if (!this.modal) return;
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    
    setTimeout(() => {
      this.searchInputMobile && this.searchInputMobile.focus();
    }, 300);
  }
  
  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.style.overflow = '';
    if (this.searchInputMobile) this.searchInputMobile.value = '';
  }
  
  toggleDrawerDropdown() {
    if (!this.drawerDropdown) return;
    if (this.drawerDropdown.hidden) {
      this.openDrawerDropdown();
    } else {
      this.closeDrawerDropdown();
    }
  }
  
  openDrawerDropdown() {
    this.renderModelList(this.drawerModelList);
    if (!this.drawerDropdown) return;
    this.drawerDropdown.hidden = false;
    this.drawerSearchInput && this.drawerSearchInput.focus();
  }
  
  closeDrawerDropdown() {
    if (!this.drawerDropdown) return;
    this.drawerDropdown.hidden = true;
    if (this.drawerSearchInput) this.drawerSearchInput.value = '';
  }
  
  toggleSectionDropdown() {
    if (!this.sectionDropdown) return;
    if (this.sectionDropdown.hidden) {
      this.openSectionDropdown();
    } else {
      this.closeSectionDropdown();
    }
  }
  
  openSectionDropdown() {
    this.renderModelList(this.sectionModelList);
    if (!this.sectionDropdown) return;
    this.sectionDropdown.hidden = false;
    this.sectionSearchInput && this.sectionSearchInput.focus();
  }
  
  closeSectionDropdown() {
    if (!this.sectionDropdown) return;
    this.sectionDropdown.hidden = true;
    if (this.sectionSearchInput) this.sectionSearchInput.value = '';
  }
  
  renderModelList(container, filter = '') {
    if (!container || !this.data) return;
    
    const filterLower = filter.toLowerCase();
    let html = '';
    
    Object.entries(this.data.models).forEach(([brand, models]) => {
      const filteredModels = filter 
        ? models.filter(m => m.toLowerCase().includes(filterLower))
        : models;
      
      if (filteredModels.length === 0) return;
      
      html += `
        <div class="model-selector__group">
          <div class="model-selector__group-title">${brand}</div>
          <div class="model-selector__group-items">
            ${filteredModels.map(model => `
              <button 
                type="button" 
                class="model-selector__item${this.selectedModel === model ? ' model-selector__item--active' : ''}" 
                data-brand="${brand}"
                data-model="${model}"
              >
                ${model}
              </button>
            `).join('')}
          </div>
        </div>
      `;
    });
    
    if (html === '') {
      html = '<div class="model-selector__no-results">Keine Modelle gefunden</div>';
    }
    
    container.innerHTML = html;
    
    container.querySelectorAll('[data-model]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const brand = e.currentTarget.dataset.brand;
        const model = e.currentTarget.dataset.model;
        this.selectModel(brand, model);
      });
    });
  }
  
  filterModels(query, container) {
    this.renderModelList(container, query);
  }
  
  selectModel(brand, model) {
    this.selectedBrand = brand;
    this.selectedModel = model;
    this.hasUserInteracted = true;
    
    this.setCookie(
      this.data.cookieName, 
      JSON.stringify({ brand, model }), 
      this.data.cookieExpiry
    );
    
    this.updateUI();
    this.closeDropdown();
    this.closeModal();
    this.closeDrawerDropdown();
    this.closeSectionDropdown();
    
    this.applyModelToPage();
    
    document.dispatchEvent(new CustomEvent('model:selected', {
      detail: { brand, model }
    }));
  }
  
  clearSelection() {
    this.selectedBrand = 'Apple';
    this.selectedModel = 'iPhone 17 Pro';
    this.hasUserInteracted = false;
    this.deleteCookie(this.data.cookieName);
    this.updateUI();
    this.applyModelToPage();
    
    document.dispatchEvent(new CustomEvent('model:cleared'));
  }
  
  updateUI() {
    // Desktop Label
    if (this.label) {
      this.label.textContent = this.hasUserInteracted
        ? this.getSelectionLabel()
        : 'z.B. iPhone 17, Galaxy S25 ...';
    }
    
    // Mobile Trigger
    if (this.mobileTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.mobileTrigger.classList.add('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.mobileTrigger.classList.remove('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    
    // Drawer Label
    if (this.drawerLabel) {
      this.drawerLabel.textContent = this.hasUserInteracted
        ? this.getSelectionLabel()
        : 'z.B. iPhone 17, Galaxy S25 ...';
    }
    
    // Drawer Trigger Style
    if (this.drawerTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.drawerTrigger.classList.add('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.drawerTrigger.classList.remove('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    
    // Section Label
    if (this.sectionLabel) {
      if (this.hasUserInteracted) {
        this.sectionLabel.textContent = this.getSelectionLabel();
      }
    }
    
    // Section Trigger Style
    if (this.sectionTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.sectionTrigger.classList.add('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.sectionTrigger.classList.remove('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
  }
  
  applyModelToPage() {
    const modelSlug = this.normalizeModelName(this.selectedModel);
    
    this.updateVariantPicker();
    this.filterProductImages(modelSlug);
    this.updateCollectionGrid(modelSlug);
  }
  
  filterProductImages(modelSlug) {
    const modelSlugLower = (modelSlug || '').toLowerCase();

    const allSlides = document.querySelectorAll('.product-main-slide');
    
    allSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      
      const srcRaw = img.src || img.dataset.src || '';
      const altRaw = img.alt || '';
      const src = srcRaw.toLowerCase();
      const alt = altRaw.toLowerCase();
      
      const containsModel = src.includes(`_${modelSlugLower}_`) || alt.includes(`_${modelSlugLower}_`);

      const hasKnownModelIdentifier =
        this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);
      
      if (hasKnownModelIdentifier && !containsModel) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden-by-model-selector', 'true');
      } else {
        slide.style.display = '';
        slide.removeAttribute('data-hidden-by-model-selector');
      }
    });
    
    // Thumbnails
    const thumbs = document.querySelectorAll('[data-product-thumbs] [data-index]');
    thumbs.forEach(thumb => {
      const img = thumb.querySelector('img');
      if (!img) return;
      
      const srcRaw = img.src || img.dataset.src || '';
      const altRaw = img.alt || '';
      const src = srcRaw.toLowerCase();
      const alt = altRaw.toLowerCase();
      
      const containsModel = src.includes(`_${modelSlugLower}_`) || alt.includes(`_${modelSlugLower}_`);
      const hasKnownModelIdentifier =
        this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);
      
      if (hasKnownModelIdentifier && !containsModel) {
        thumb.style.display = 'none';
        thumb.setAttribute('data-hidden-by-model-selector', 'true');
      } else {
        thumb.style.display = '';
        thumb.removeAttribute('data-hidden-by-model-selector');
      }
    });
    
    this.rebuildFlickity();
  }
  
  rebuildFlickity() {
    const slideshowContainer = document.querySelector('.product-slideshow');
    if (!slideshowContainer) return;
    
    const existingFlickity = window.Flickity?.data(slideshowContainer);
    if (!existingFlickity) return;
    
    if (!this.flickityConfig) {
      this.flickityConfig = { ...existingFlickity.options };
    }
    
    const currentHeight = slideshowContainer.offsetHeight;
    slideshowContainer.style.minHeight = `${currentHeight}px`;
    
    const allSlides = slideshowContainer.querySelectorAll('.product-main-slide');
    const visibleSlides = Array.from(allSlides).filter(slide => 
      !slide.hasAttribute('data-hidden-by-model-selector')
    );
    
    if (visibleSlides.length === 0) return;
    
    existingFlickity.destroy();
    
    setTimeout(() => {
      const newFlickity = new Flickity(slideshowContainer, {
        ...this.flickityConfig,
        cellSelector: '.product-main-slide:not([data-hidden-by-model-selector])'
      });
      
      if (newFlickity.slides.length > 0) {
        newFlickity.select(0, false, true);
      }
      
      setTimeout(() => {
        slideshowContainer.style.minHeight = '';
      }, 200);
      
      this.rebindThumbnailClicks(newFlickity);
    }, 100);
  }

  rebindThumbnailClicks(flickityInstance) {
    const thumbs = document.querySelectorAll('[data-product-thumbs] [data-index]');
    
    thumbs.forEach(thumb => {
      const newThumb = thumb.cloneNode(true);
      thumb.parentNode.replaceChild(newThumb, thumb);
      
      newThumb.addEventListener('click', (e) => {
        e.preventDefault();
        
        if (newThumb.hasAttribute('data-hidden-by-model-selector')) {
          return;
        }
        
        const index = parseInt(newThumb.dataset.index);
        
        const allSlides = document.querySelectorAll('.product-main-slide');
        const visibleSlides = Array.from(allSlides).filter(slide => 
          !slide.hasAttribute('data-hidden-by-model-selector')
        );
        
        let targetIndex = 0;
        for (let i = 0; i < allSlides.length; i++) {
          if (allSlides[i].hasAttribute('data-hidden-by-model-selector')) {
            continue;
          }
          if (i === index) {
            break;
          }
          targetIndex++;
        }
        
        if (flickityInstance && targetIndex < visibleSlides.length) {
          flickityInstance.select(targetIndex, false, true);
        }
        
        document.querySelectorAll('[data-product-thumbs] [data-index]').forEach(t => {
          t.classList.remove('is-active', 'is-selected');
        });
        newThumb.classList.add('is-active', 'is-selected');
      });
    });
  }
  
  updateCollectionGrid(modelSlug) {
    const gridItems = document.querySelectorAll('.grid-product[data-product-handle]');
    
    gridItems.forEach(item => {
      const productHandle = item.dataset.productHandle;
      if (!productHandle) return;
      
      this.updateGridItemImage(item, modelSlug);
    });
  }
  
  /**
   * PERFORMANCE-OPTIMIERTE CROSS-FADE METHODE
   * - Bild-Cache für schnelleres Laden
   * - Verhindert mehrfache gleichzeitige Transitions
   * - RequestAnimationFrame für optimales Timing
   * - Wartet bis Bild komplett geladen ist
   */
  updateGridItemImage(gridItem, modelSlug) {
    const productId = gridItem.dataset.productId;
    if (!productId) return;
    
    const img = gridItem.querySelector('.grid-product__main-image');
    if (!img) return;
    
    // Verhindere mehrfache Transitions auf dem gleichen Element
    const transitionKey = `${productId}-${modelSlug}`;
    if (this.pendingTransitions.has(transitionKey)) return;
    
    fetch(`/products/${gridItem.dataset.productHandle}.js`)
      .then(res => res.json())
      .then(product => {
        const matchingVariant = product.variants.find(v => {
          const option2 = v.option2;
          return option2 && this.normalizeModelName(option2) === modelSlug;
        });
        
        if (matchingVariant && matchingVariant.featured_image) {
          const newImageSrc = matchingVariant.featured_image.src;
          
          // Prüfen ob das Bild bereits angezeigt wird
          if (img.src === newImageSrc) return;
          
          // Transition als pending markieren
          this.pendingTransitions.add(transitionKey);
          
          // WICHTIG: Erst das Bild komplett laden, dann Transition starten
          this.preloadImage(newImageSrc).then(preloadedImg => {
            const imageWrapper = img.parentElement;
            
            // Prüfe ob bereits ein Overlay existiert (Race Condition)
            const existingOverlay = imageWrapper.querySelector('.grid-product__transition-overlay');
            if (existingOverlay) {
              existingOverlay.remove();
            }
            
            // Overlay-Bild erstellen (ohne cloneNode für bessere Performance)
            const overlayImg = document.createElement('img');
            overlayImg.className = img.className + ' grid-product__transition-overlay';
            overlayImg.src = newImageSrc;
            overlayImg.srcset = this.generateSrcset(newImageSrc);
            overlayImg.alt = img.alt;
            
            // Inline Styles für Performance (kein CSS Parse)
            Object.assign(overlayImg.style, {
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              opacity: '0',
              zIndex: '2',
              pointerEvents: 'none',
              willChange: 'opacity'
            });
            
            // Position relative setzen falls nötig
            const wrapperPosition = window.getComputedStyle(imageWrapper).position;
            if (wrapperPosition === 'static') {
              imageWrapper.style.position = 'relative';
            }
            
            imageWrapper.appendChild(overlayImg);
            
            // Warte bis Overlay-Bild auch im DOM geladen ist
            if (overlayImg.complete) {
              // Bild ist bereits im Cache/geladen
              this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
            } else {
              // Warte auf load event des Overlay-Bildes
              overlayImg.onload = () => {
                this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
              };
              
              // Fallback timeout falls onload nicht feuert
              setTimeout(() => {
                if (overlayImg.parentElement) {
                  this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
                }
              }, 1000);
            }
          }).catch(err => {
            // Fallback ohne Transition
            console.warn('Failed to preload image:', newImageSrc, err);
            img.src = newImageSrc;
            img.srcset = this.generateSrcset(newImageSrc);
            this.pendingTransitions.delete(transitionKey);
          });
        }
      })
      .catch(err => {
        console.error('Failed to fetch product data:', err);
        this.pendingTransitions.delete(transitionKey);
      });
  }
  
  /**
   * Startet die Cross-Fade Animation
   */
  startCrossFade(overlayImg, originalImg, newImageSrc, transitionKey) {
    // Force reflow für CSS Transition
    overlayImg.offsetHeight;
    
    // Fade in mit requestAnimationFrame für optimales Timing
    requestAnimationFrame(() => {
      overlayImg.style.transition = 'opacity 0.4s ease-out';
      overlayImg.style.opacity = '1';
    });
    
    // Cleanup nach Transition
    setTimeout(() => {
      originalImg.src = newImageSrc;
      originalImg.srcset = this.generateSrcset(newImageSrc);
      
      if (overlayImg.parentElement) {
        overlayImg.remove();
      }
      
      // Transition beendet
      this.pendingTransitions.delete(transitionKey);
    }, 400);
  }
  
  /**
   * Preload Image mit Cache
   */
  preloadImage(src) {
    // Check Cache
    if (this.imageCache.has(src)) {
      return Promise.resolve(this.imageCache.get(src));
    }
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.imageCache.set(src, img);
        resolve(img);
      };
      
      img.onerror = reject;
      img.src = src;
    });
  }
  
  updateVariantPicker() {
    this._updatingVariantPicker = true;
    
    const brandContainer = document.querySelector('[data-index="option1"]');
    const modelContainer = document.querySelector('[data-index="option2"]');
    
    if (!brandContainer || !modelContainer) {
      this._updatingVariantPicker = false;
      return;
    }
    
    // UPDATE BRAND (Option 1)
    const brandSelect = brandContainer.querySelector('select');
    if (brandSelect) {
      if (this.hasUserInteracted) {
        if (brandSelect.value !== this.selectedBrand) {
          brandSelect.value = this.selectedBrand;
          brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        brandSelect.value = '';
      }
    } else {
      const brandInputs = brandContainer.querySelectorAll('input[data-variant-input]');
      brandInputs.forEach(input => {
        if (this.hasUserInteracted && input.value === this.selectedBrand) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (!this.hasUserInteracted) {
          input.checked = false;
        }
      });
    }
    
    // UPDATE MODEL (Option 2)
    setTimeout(() => {
      const modelSelect = modelContainer.querySelector('select');
      
      if (modelSelect) {
        if (this.hasUserInteracted) {
          if (modelSelect.disabled) {
            modelSelect.disabled = false;
          }
          
          const modelOption = Array.from(modelSelect.options).find(o => 
            o.value === this.selectedModel
          );
          
          if (modelOption && modelSelect.value !== this.selectedModel) {
            modelSelect.value = this.selectedModel;
            modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          modelSelect.value = '';
          modelSelect.disabled = true;
        }
      } else {
        const modelInputs = modelContainer.querySelectorAll('input[data-variant-input]');
        modelInputs.forEach(input => {
          if (this.hasUserInteracted && input.value === this.selectedModel) {
            if (!input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else if (!this.hasUserInteracted) {
            input.checked = false;
            input.disabled = true;
          }
        });
      }
      
      if (this.hasUserInteracted) {
        this.triggerShopifyVariantUpdate();
      }
      this._updatingVariantPicker = false;
    }, 200);
  }
  
  triggerShopifyVariantUpdate() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    
    const variantIdInput = form.querySelector('select[name="id"]');
    if (variantIdInput) {
      variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    const shopifySelect = document.querySelector('[name="id"]');
    if (shopifySelect) {
      shopifySelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  
  syncWithVariantPicker(variant) {
    if (this._updatingVariantPicker) return;
    
    if (variant.option1 && variant.option2) {
      const brand = variant.option1;
      const model = variant.option2;
      
      this.hasUserInteracted = true;
      
      if (this.selectedBrand !== brand || this.selectedModel !== model) {
        this._syncingFromVariantPicker = true;
        
        this.selectedBrand = brand;
        this.selectedModel = model;
        
        this.setCookie(
          this.data.cookieName, 
          JSON.stringify({ brand, model }), 
          this.data.cookieExpiry
        );
        
        this.updateUI();
        this.applyModelToPage();
        
        if (this.isMobileViewport()) {
          setTimeout(() => {
            const productImages = document.querySelector('.product-slideshow') || 
                                 document.querySelector('.product-images') ||
                                 document.querySelector('.product-single__photos');

            if (productImages) {
              productImages.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 300);
        }
        
        document.dispatchEvent(new CustomEvent('model:selected', {
          detail: { brand, model }
        }));
        
        this._syncingFromVariantPicker = false;
      }
    }
  }
  
  normalizeModelName(model) {
    return model
      .replace(/\s+/g, '-')
      .replace(/\+/g, '-Plus')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
  
  generateSrcset(src) {
    const widths = [160, 240, 360, 420];
    return widths.map(w => {
      const url = src.replace(/\.(jpg|jpeg|png|webp)/i, `_${w}x.$1`);
      return `${url} ${w}w`;
    }).join(', ');
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  // Cookie helpers
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }
  
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
  
  deleteCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.modelSelectorInstance = new ModelSelector();
  });
} else {
  window.modelSelectorInstance = new ModelSelector();
}

// Export for potential external use
window.CasartoModelSelector = ModelSelector;